import React, { useEffect, useState } from "react";
import { Plus, Trash2, X, Pencil, ExternalLink, Gauge } from "lucide-react";
import { Link } from "react-router-dom";
import {
  adminListBilanci,
  adminCreateBilancio,
  adminUpdateBilancio,
  adminDeleteBilancio,
} from "../../api";
import TrasparenzaEditor from "./TrasparenzaEditor";

const TEMPLATE = {
  ticker: "",
  società: "",
  settore: "",
  mercato: "Euronext Growth Milan (EGM)",
  data_quotazione_egm: "",
  anno_bilancio: "2025",
  nota_speciale: "",
  ricavi: { anno_corrente: 0, anno_precedente: 0, variazione_percentuale: 0 },
  ebitda: { valore: 0, margin_percentuale: 0 },
  utile_netto: { valore: 0, variazione_percentuale: 0, positivo: false },
  posizione_finanziaria_netta: { valore: 0, giudizio: "attenzione" },
  punti_di_forza: ["", "", ""],
  rischi: ["", "", ""],
  giudizio_redazionale: "",
  semaforo_salute: "giallo",
  motivazione_semaforo: "",
};

const BilancioManager = () => {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null); // {id|null}
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [trEditing, setTrEditing] = useState(null);

  const load = async () => {
    try {
      setItems(await adminListBilanci());
    } catch (e) {
      setItems([]);
    }
  };
  useEffect(() => {
    load();
  }, []);

  const openNew = () => {
    setEditing({ id: null });
    setText(JSON.stringify(TEMPLATE, null, 2));
    setMsg(null);
  };
  const openEdit = (b) => {
    const copy = { ...b };
    delete copy._id;
    setEditing({ id: b.id });
    setText(JSON.stringify(copy, null, 2));
    setMsg(null);
  };

  const save = async () => {
    setMsg(null);
    let data;
    try {
      data = JSON.parse(text);
    } catch (e) {
      setMsg({ type: "error", text: "JSON non valido: controlla la sintassi." });
      return;
    }
    setBusy(true);
    try {
      if (editing.id) await adminUpdateBilancio(editing.id, data);
      else await adminCreateBilancio(data);
      setEditing(null);
      await load();
    } catch (err) {
      setMsg({ type: "error", text: err?.response?.data?.detail || "Errore nel salvataggio." });
    } finally {
      setBusy(false);
    }
  };

  const remove = async (b) => {
    if (!window.confirm(`Eliminare la scheda "${b.societa}"?`)) return;
    await adminDeleteBilancio(b.id);
    await load();
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div>
          <h2 className="font-serif-display text-2xl text-sq-navy">Bilancio in 60 secondi (EGM)</h2>
          <p className="text-sm text-gray-500">{items.length} schede società · aggiungi tramite JSON</p>
        </div>
        {!editing && (
          <button
            onClick={openNew}
            className="flex items-center gap-2 bg-sq-orange hover:bg-sq-orange-dark transition-colors text-white px-4 py-2.5 rounded-sm text-sm font-medium"
          >
            <Plus size={16} /> Nuova scheda
          </button>
        )}
      </div>

      {editing && (
        <div className="bg-white border border-sq rounded-md p-6 mb-8">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-medium text-sq-navy">{editing.id ? "Modifica scheda" : "Nuova scheda"} (JSON)</h3>
            <button onClick={() => setEditing(null)} className="text-gray-400 hover:text-sq-navy">
              <X size={20} />
            </button>
          </div>
          {msg && (
            <div className={`mb-3 text-sm rounded-sm px-3 py-2 ${msg.type === "error" ? "bg-red-50 border border-red-200 text-red-600" : "bg-emerald-50 border border-emerald-200 text-emerald-700"}`}>
              {msg.text}
            </div>
          )}
          <p className="text-xs text-gray-500 mb-2">
            Modifica i campi. Usa <code>"semaforo_salute"</code>: verde / giallo / rosso. Valori monetari in migliaia di €.
          </p>
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={20}
            spellCheck={false}
            className="w-full border border-sq rounded-sm px-3 py-2.5 text-xs font-mono outline-none focus:border-sq-orange"
          />
          <div className="flex gap-3 mt-4">
            <button
              onClick={save}
              disabled={busy}
              className="bg-sq-orange hover:bg-sq-orange-dark transition-colors text-white px-5 py-2.5 rounded-sm text-sm font-medium disabled:opacity-60"
            >
              {busy ? "Salvataggio..." : "Salva scheda"}
            </button>
            <button onClick={() => setEditing(null)} className="border border-sq text-sq-navy px-5 py-2.5 rounded-sm text-sm hover:bg-sq-light">
              Annulla
            </button>
          </div>
        </div>
      )}

      <div className="bg-white border border-sq rounded-md overflow-hidden">
        <div className="divide-y divide-[#e6e8ec]">
          {items.map((b) => (
            <div key={b.id} className="flex items-center gap-4 px-4 py-3 hover:bg-sq-light">
              <span className="text-xl">{b.semaforo_salute === "verde" ? "🟢" : b.semaforo_salute === "rosso" ? "🔴" : "🟡"}</span>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-sq-navy">{b.societa}</span>
                  <span className="text-[10px] bg-sq-orange text-white font-bold px-1.5 rounded">EGM</span>
                  <span className="text-xs text-gray-400">{b.ticker}</span>
                </div>
                <span className="text-xs text-gray-400">{b.settore}</span>
              </div>
              <Link to={`/bilancio-60-secondi/${b.ticker}`} target="_blank" className="text-gray-400 hover:text-sq-orange p-2" title="Vedi scheda">
                <ExternalLink size={15} />
              </Link>
              <button onClick={() => setTrEditing(b)} className="text-gray-400 hover:text-sq-orange p-2 flex items-center gap-1" title="Trasparenza">
                <Gauge size={16} />
                <span className="text-xs hidden sm:inline">Trasparenza{b.trasparenza ? ` (${b.trasparenza.punteggio_totale}/100)` : ""}</span>
              </button>
              <button onClick={() => openEdit(b)} className="text-gray-400 hover:text-sq-orange p-2">
                <Pencil size={16} />
              </button>
              <button onClick={() => remove(b)} className="text-gray-400 hover:text-red-600 p-2">
                <Trash2 size={16} />
              </button>
            </div>
          ))}
          {items.length === 0 && <div className="px-4 py-8 text-center text-gray-500 text-sm">Nessuna scheda.</div>}
        </div>
      </div>
      {trEditing && (
        <TrasparenzaEditor
          bilancio={trEditing}
          onClose={() => setTrEditing(null)}
          onSaved={load}
        />
      )}
    </div>
  );
};

export default BilancioManager;
